import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png';

export default function LoginPage() {
  const { login, authProvider, startEntraLogin, completeEntraLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [entraLoading, setEntraLoading] = useState(false);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const oauthError = searchParams.get('error');

    if (oauthError) {
      setError('Microsoft-Anmeldung wurde abgebrochen oder ist fehlgeschlagen.');
      return;
    }

    if (authProvider !== 'entra' || !code || !state) return;

    let active = true;
    const finishLogin = async () => {
      setEntraLoading(true);
      setError('');
      try {
        await completeEntraLogin(code, state);
        if (active) navigate('/', { replace: true });
      } catch (err) {
        if (!active) return;
        const msg = err.response?.data?.error || 'Microsoft-Anmeldung fehlgeschlagen.';
        setError(msg);
      } finally {
        if (active) setEntraLoading(false);
      }
    };

    finishLogin();
    return () => {
      active = false;
    };
  }, [authProvider, completeEntraLogin, navigate, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.error || 'Anmeldung fehlgeschlagen.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError('');
    setEntraLoading(true);
    try {
      await startEntraLogin();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Microsoft-Anmeldung konnte nicht gestartet werden.';
      setError(msg);
      setEntraLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f1f5f9' }}>
      <div className="w-full max-w-md">

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">

          {/* Header */}
          <div className="px-8 pt-8 pb-6 border-b border-gray-100 flex flex-col items-center gap-4">
            <div className="bg-hrf-navy rounded-xl px-4 py-2.5">
              <img src={logo} alt="HR Factory" className="h-8 w-auto" />
            </div>
            <div className="text-center">
              <h1 className="text-xl font-semibold text-slate-800">Anmelden</h1>
              <p className="text-sm text-slate-500 mt-1">Schön, dass du wieder da bist.</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-8 py-7 space-y-5">

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {authProvider === 'entra' ? (
              <button
                type="button"
                onClick={handleMicrosoftLogin}
                disabled={entraLoading}
                className="w-full bg-hrf-navy hover:bg-hrf-navy-800 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm py-2.5 rounded-lg transition-colors duration-150"
              >
                {entraLoading ? 'Weiterleitung…' : 'Mit Microsoft anmelden'}
              </button>
            ) : (
              <>
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-700" htmlFor="email">
                    E-Mail
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@unternehmen.de"
                    className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm text-slate-900
                               placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-hrf-navy
                               focus:border-transparent transition"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-700" htmlFor="password">
                    Passwort
                  </label>
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm text-slate-900
                               placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-hrf-navy
                               focus:border-transparent transition"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-hrf-navy hover:bg-hrf-navy-800 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm py-2.5 rounded-lg transition-colors duration-150"
                >
                  {loading ? 'Anmelden…' : 'Anmelden'}
                </button>
              </>
            )}

          </form>

          {/* Footer hint */}
          <div className="px-8 pb-7">
            <p className="text-xs text-center text-slate-400">
              Noch kein Zugang? Dann einfach eine Einladung anfragen – wir melden uns.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}
